import {
  ISecrEnergySourceLine,
  ISecrReportFiguresGroup,
  ISecrSourceMetrics,
} from '../../../../shared/services/generated-report/generated-report.model';
import {
  isScope1ApplicableForSourceKey,
  isScope2ApplicableForSourceKey,
  type SecrSourceMetricField,
} from './secr-source-applicability';

const EPS = 0.02;

/** UI-only labels; internal API keys unchanged. */
export function displaySourceLabel(sourceKey: string, apiLabel: string): string {
  if (sourceKey === 'unmapped') {
    return 'Other / not yet classified';
  }
  return apiLabel;
}

export function shouldShowUnmappedRow(line: ISecrEnergySourceLine): boolean {
  if (line.key !== 'unmapped') return true;
  const fields: (keyof ISecrSourceMetrics)[] = [
    'energyKwh',
    'scope1Tco2e',
    'scope2LocationTco2e',
    'scope2MarketTco2e',
  ];
  for (const seg of ['uk', 'overseas', 'global'] as const) {
    for (const f of fields) {
      const v = line[seg][f];
      if (v != null && Number.isFinite(Number(v)) && Math.abs(Number(v)) > EPS) {
        return true;
      }
    }
  }
  return false;
}

/** Sum for rows matching predicate; null if no matching rows or no numeric inputs (all null → –). */
function aggregateForTotal(
  lines: ISecrEnergySourceLine[],
  seg: 'uk' | 'overseas' | 'global',
  field: keyof ISecrSourceMetrics,
  includeLine: (sourceKey: string) => boolean
): number | null {
  const relevant = lines.filter((l) => includeLine(l.key));
  if (relevant.length === 0) return null;
  let sum = 0;
  let anyNumeric = false;
  for (const l of relevant) {
    const v = l[seg][field];
    if (v != null && Number.isFinite(Number(v))) {
      anyNumeric = true;
      sum += Number(v);
    }
  }
  if (!anyNumeric) return null;
  return sum;
}

function fieldToApplicability(
  field: SecrSourceMetricField
): (sourceKey: string) => boolean {
  switch (field) {
    case 'energyKwh':
      return () => true;
    case 'scope1Tco2e':
      return isScope1ApplicableForSourceKey;
    case 'scope2LocationTco2e':
    case 'scope2MarketTco2e':
      return isScope2ApplicableForSourceKey;
    default:
      return () => false;
  }
}

/** Aggregates visible data rows into a Total line; nulls become – in the UI (no fake zeros). */
export function buildSecrSourceTotalLine(lines: ISecrEnergySourceLine[]): ISecrEnergySourceLine {
  const e = fieldToApplicability('energyKwh');
  const s1 = fieldToApplicability('scope1Tco2e');
  const s2l = fieldToApplicability('scope2LocationTco2e');
  const s2m = fieldToApplicability('scope2MarketTco2e');
  return {
    key: 'total',
    label: 'Total',
    uk: {
      energyKwh: aggregateForTotal(lines, 'uk', 'energyKwh', e),
      scope1Tco2e: aggregateForTotal(lines, 'uk', 'scope1Tco2e', s1),
      scope2LocationTco2e: aggregateForTotal(lines, 'uk', 'scope2LocationTco2e', s2l),
      scope2MarketTco2e: aggregateForTotal(lines, 'uk', 'scope2MarketTco2e', s2m),
    },
    overseas: {
      energyKwh: aggregateForTotal(lines, 'overseas', 'energyKwh', e),
      scope1Tco2e: aggregateForTotal(lines, 'overseas', 'scope1Tco2e', s1),
      scope2LocationTco2e: aggregateForTotal(lines, 'overseas', 'scope2LocationTco2e', s2l),
      scope2MarketTco2e: aggregateForTotal(lines, 'overseas', 'scope2MarketTco2e', s2m),
    },
    global: {
      energyKwh: aggregateForTotal(lines, 'global', 'energyKwh', e),
      scope1Tco2e: aggregateForTotal(lines, 'global', 'scope1Tco2e', s1),
      scope2LocationTco2e: aggregateForTotal(lines, 'global', 'scope2LocationTco2e', s2l),
      scope2MarketTco2e: aggregateForTotal(lines, 'global', 'scope2MarketTco2e', s2m),
    },
  };
}

export type SecrBreakdownValidation = {
  ok: boolean;
  messages: string[];
};

/**
 * Compares sums of source rows to headline figures for the same period and geography.
 */
export function validateSecrBreakdown(
  period: ISecrReportFiguresGroup,
  lines: ISecrEnergySourceLine[],
  includeGlobal: boolean
): SecrBreakdownValidation {
  const dataLines = lines.filter((l) => l.key !== 'total');
  const messages: string[] = [];

  const check = (
    name: string,
    sumUk: number,
    sumOs: number,
    sumGl: number,
    refUk: number,
    refOs: number,
    refGl: number
  ) => {
    const cmp = (a: number, b: number) => Math.abs(a - b) <= EPS;
    if (!cmp(sumUk, refUk)) {
      messages.push(`${name} UK: breakdown ${sumUk.toFixed(4)} vs summary ${refUk.toFixed(4)}`);
    }
    if (includeGlobal) {
      if (!cmp(sumOs, refOs)) {
        messages.push(`${name} Overseas: breakdown ${sumOs.toFixed(4)} vs summary ${refOs.toFixed(4)}`);
      }
      if (!cmp(sumGl, refGl)) {
        messages.push(`${name} Total: breakdown ${sumGl.toFixed(4)} vs summary ${refGl.toFixed(4)}`);
      }
    }
  };

  const sumField = (field: keyof ISecrSourceMetrics, seg: 'uk' | 'overseas' | 'global') =>
    dataLines.reduce((s, l) => s + (l[seg][field] != null && Number.isFinite(Number(l[seg][field])) ? Number(l[seg][field]) : 0), 0);

  const n = (x: number | undefined) => (x != null && Number.isFinite(x) ? Number(x) : 0);

  check(
    'Energy (kWh)',
    sumField('energyKwh', 'uk'),
    sumField('energyKwh', 'overseas'),
    sumField('energyKwh', 'global'),
    n(period.kwh?.uk),
    n(period.kwh?.overseas),
    n(period.kwh?.global)
  );
  check(
    'Scope 1 (tCO2e)',
    sumField('scope1Tco2e', 'uk'),
    sumField('scope1Tco2e', 'overseas'),
    sumField('scope1Tco2e', 'global'),
    n(period.emissions?.scopeOne?.uk),
    n(period.emissions?.scopeOne?.overseas),
    n(period.emissions?.scopeOne?.global)
  );
  check(
    'Scope 2 location-based (tCO2e)',
    sumField('scope2LocationTco2e', 'uk'),
    sumField('scope2LocationTco2e', 'overseas'),
    sumField('scope2LocationTco2e', 'global'),
    n(period.emissions?.scopeTwoLocation?.uk),
    n(period.emissions?.scopeTwoLocation?.overseas),
    n(period.emissions?.scopeTwoLocation?.global)
  );
  check(
    'Scope 2 market-based (tCO2e)',
    sumField('scope2MarketTco2e', 'uk'),
    sumField('scope2MarketTco2e', 'overseas'),
    sumField('scope2MarketTco2e', 'global'),
    n(period.emissions?.scopeTwoMarket?.uk),
    n(period.emissions?.scopeTwoMarket?.overseas),
    n(period.emissions?.scopeTwoMarket?.global)
  );

  return { ok: messages.length === 0, messages };
}

export function logSecrBreakdownValidationDev(periodName: string, result: SecrBreakdownValidation): void {
  const isDev =
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
  if (isDev && !result.ok) {
    // eslint-disable-next-line no-console
    console.warn(`[SECR source breakdown] Reconciliation mismatch for ${periodName}:`, result.messages);
  }
}
